import type {RuleBlockComponentProps, RuleData} from 'src/types/referral-v2';
import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';

import classify from 'src/utils/classify';
import {getRuleInfo} from 'src/components/referral-v2/common';
import {Dropdown} from '@spaced-out/ui-design-system/lib/components/Dropdown';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {Link} from '@spaced-out/ui-design-system/lib/components/Link';
import {
  FormLabelSmall,
  SubTitleExtraSmall,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {Input} from '@spaced-out/ui-design-system/lib/components/Input';
import {Card} from '@spaced-out/ui-design-system/lib/components/Card';
import {
  RadioGroup,
  RadioButton,
} from '@spaced-out/ui-design-system/lib/components/RadioButton';
import Rule from 'src/components/referral-v2/payout-rule/rule';
import rulesCss from 'src/components/referral-v2/settings/settings.css';


const StartDateRuleBlock = ({
  getEntityOptionsByFieldType,
  handleUpdateRuleBlockData,
  handleSelectRule,
  handleCopyRule,
  handleAddRule,
  ruleBlockData,
  entityFields,
  onDeleteRule,
  onEditRule,
  disabled,
}: RuleBlockComponentProps) => {
  const {t} = useLabelTranslation();
  const handleChangeRuleType = (value: string) => {
    let data = {rule_type: value};
    if (value === 'field') {
      // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'field' does not exist in type '{ rule_type: string; }'.
      data = {...data, field: {field_name: ''}};
    } else {
      // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'rule' does not exist in type '{ rule_type: string; }'.
      data = {...data, rule: []};
    }
    handleUpdateRuleBlockData('start_date_rule', data);
  };

  React.useEffect(() => {
    if (!ruleBlockData) {
      handleUpdateRuleBlockData('start_date_rule', {
        rule_type: 'field',
        field: {
          field_name: '',
        },
      });
    }
  }, []);

  const handleOffsetChange = (value: string) => {
    if (parseInt(value) > 300) {
      return;
    }
    if (ruleBlockData) {
      handleUpdateRuleBlockData('start_date_rule', {
        ...ruleBlockData,
        offset: parseInt(value),
      });
    }
  };

  return (
    <div className={classify(rulesCss.flexColumn, rulesCss.defaultGap)}>
      <Link onClick={() => handleCopyRule?.()} disabled={disabled}>{`${t(
        'Use_the_rule_as_created_in_1_above',
        'Use the rule as created in 1 above',
      )}.`}</Link>
      <RadioGroup
        align="horizontal-fixed"
        name="start-date-rule"
        onChange={handleChangeRuleType}
        value={ruleBlockData?.rule_type ?? 'field'}
      >
        <RadioButton disabled={disabled} value={'field'}>
          {t(
            'Map_a_field_to_get_the_value_automatically',
            'Map a field to get the value automatically',
          )}
        </RadioButton>
        <RadioButton disabled={disabled} value={'event'}>
          {t('Configure_rule', 'Configure rule')}
        </RadioButton>
      </RadioGroup>
      {ruleBlockData?.rule_type === 'event' ? (
        <>
          {ruleBlockData.rule?.map((payoutRule: RuleData, index: number) => {
            const rule = entityFields.find(
              (field) => field.value === payoutRule.field_name,
            );
            return (
              <Rule
                disabled={disabled}
                options={getEntityOptionsByFieldType()}
                rule={rule}
                fieldObject={payoutRule}
                showAndText={index > 0}
                hideRemoveIcon={index === 0}
                handleSelectRule={(option) =>
                  handleSelectRule(
                    option,
                    'start_date_rule',
                    ruleBlockData,
                    index,
                  )
                }
                onRemove={() =>
                  onDeleteRule(index, 'start_date_rule', ruleBlockData)
                }
                onEdit={(ruleData) =>
                  onEditRule(ruleData, 'start_date_rule', ruleBlockData, index)
                }
              />
            );
          })}
          {ruleBlockData.rule && ruleBlockData.rule.length > 0 && (
            <div
              className={classify(rulesCss.offSetInput, rulesCss.defaultGap)}
            >
              <div className={rulesCss.andText}></div>
              <FormLabelSmall color="secondary">{`${t(
                'From_the_date_captured_above_add',
                'From the date captured above, add',
              )}:`}</FormLabelSmall>
              <div className={rulesCss.ruleInput}>
                <Input
                  onChange={(event) => handleOffsetChange(event.target.value)}
                  disabled={disabled}
                  value={
                    ruleBlockData.offset ? ruleBlockData.offset.toString() : ''
                  }
                  min="0"
                  size="small"
                  type="number"
                />
              </div>
              <FormLabelSmall>{t('days', 'days')}</FormLabelSmall>
            </div>
          )}
          <div className={rulesCss.ruleInputContainer}>
            <Button
              size="small"
              iconLeftName="plus"
              isFluid={false}
              type="secondary"
              disabled={disabled}
              onClick={() => handleAddRule('start_date_rule', ruleBlockData)}
            >
              {t('Add_a_new_clause', 'Add a new clause')}
            </Button>
          </div>
          {ruleBlockData.rule &&
            ruleBlockData.rule.filter((ruleObj) => ruleObj.field_name).length >
              0 && (
              <Card classNames={{wrapper: rulesCss.chip}}>
                <SubTitleExtraSmall color="secondary">
                  {t(
                    'We_will_use_the_date_when_the_condition_defined_above_is_satisfied_to_identify_the_start_date_for_the_referral_candidate_In_this_case_when_the_field',
                    'We will use the date when the condition defined above is satisfied to identify the start date for the referral candidate. In this case, when the field',
                  )}
                  {getRuleInfo(ruleBlockData.rule)},{' '}
                  {!!ruleBlockData.offset &&
                    `${t(
                      'and_identify_the_start_date_after',
                      'and identify the start date after',
                    )} ${ruleBlockData.offset ?? ''} ${t(
                      'days_for_the_referral_candidate',
                      'days for the referral candidate',
                    )}`}
                </SubTitleExtraSmall>
              </Card>
            )}
        </>
      ) : (
        <>
          <div className={rulesCss.ruleInput}>
            <Dropdown
              required={true}
              disabled={disabled}
              size="small"
              label={t('Field_name', 'Field name')}
              dropdownInputText={ruleBlockData?.field?.field_name ?? 'Select'}
              onChange={(option) =>
                handleSelectRule(option, 'start_date_rule', ruleBlockData)
              }
              menu={{
                classNames: {
                  wrapper: rulesCss.option,
                  optionTextContainer: rulesCss.option,
                },
                options: getEntityOptionsByFieldType('date') ?? [],
                selectedKeys: ruleBlockData?.field?.field_name
                  ? [ruleBlockData.field.field_name]
                  : [],
                allowSearch: true,
                staticLabels: {
                  SEARCH_PLACEHOLDER: `${t('Search', 'Search')}...`,
                  RESULT: t('Result', 'Result'),
                  RESULTS: t('Results', 'Results'),
                },
              }}
            />
          </div>
          {ruleBlockData?.field?.field_name && (
            <Card classNames={{wrapper: rulesCss.chip}}>
              <SubTitleExtraSmall color="secondary">
                {`${t(
                  'We_will_use_the_date_shown_in',
                  'We will use the date shown in',
                )} "`}
                {ruleBlockData.field?.field_name}
                {`"
                ${t(
                  'field_to_identify_the_start_date_of_referral_candidate',
                  'field to identify the start date of referral candidate',
                )}.`}
              </SubTitleExtraSmall>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default StartDateRuleBlock;
